import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertCircle, QrCode, X, Search, Phone, Share2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import QrReader from 'react-qr-scanner';

const countryCodes = [
  { code: '+967', country: 'اليمن' },
  { code: '+249', country: 'السودان' },
  { code: '+20', country: 'مصر' },
  { code: '+966', country: 'السعودية' },
];


interface CustomerSearchResult {
  id: string;
  username: string;
  phone: string;
  country_code: string;
  smart_key: string;
  account_type: string;
  isExisting?: boolean;
}

interface InvitationData {
  phone: string;
  countryCode: string;
  address: string;
  shopOwnerName: string;
  shopName: string;
}

function AddCustomer() {
  const navigate = useNavigate();
  const [customerSmartKey, setCustomerSmartKey] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+967');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<Profile | null>(null);
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState<CustomerSearchResult | null>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationPhone, setInvitationPhone] = useState('');
  const [invitationCountryCode, setInvitationCountryCode] = useState('+967');
  const [invitationAddress, setInvitationAddress] = useState('');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerAccountType, setCustomerAccountType] = useState('customer');
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsSupported, setContactsSupported] = useState(false);

  useEffect(() => {
    fetchVendorProfile();
    checkContactsSupport();
  }, []);

  const checkContactsSupport = () => {
    // Check if the Contacts API is supported
    const supported = 'contacts' in navigator && 'ContactsManager' in window;
    setContactsSupported(supported);
  };

  const handleContactsAccess = async () => {
    try {
      setContactsLoading(true);
      setShowContactsModal(true);
     let uniquePhones: string[] = [];
      
      // Check if contacts API is supported
      if (!('contacts' in navigator)) {
        throw new Error('هذه الميزة غير متاحة في متصفحك الحالي');
      }
      
      // Request contacts access
      const contacts = await (navigator as any).contacts.select(['tel'], {
        multiple: true
      });
      
      // Process and format contacts
      const phoneNumbers: string[] = [];
      
      for (const contact of contacts) {
        if (contact.tel && contact.tel.length > 0) {
          for (const tel of contact.tel) {
            // Clean and format phone number
            let phone = tel.replace(/\D/g, ''); // Remove all non-digits
            
            // Skip very short numbers
            if (phone.length < 7) continue;
            
            // Remove country codes if they exist
            if (phone.startsWith('967') && phone.length > 9) {
              phone = phone.substring(3);
            } else if (phone.startsWith('249') && phone.length > 9) {
              phone = phone.substring(3);
            } else if (phone.startsWith('20') && phone.length > 8) {
              phone = phone.substring(2);
            } else if (phone.startsWith('966') && phone.length > 9) {
              phone = phone.substring(3);
            } else if (phone.startsWith('00967')) {
              phone = phone.substring(5);
            } else if (phone.startsWith('00249')) {
              phone = phone.substring(5);
            } else if (phone.startsWith('0020')) {
              phone = phone.substring(4);
            } else if (phone.startsWith('00966')) {
              phone = phone.substring(5);
            }
            
            // Remove leading zeros
            phone = phone.replace(/^0+/, '');
            
            if (phone.length >= 7) {
              phoneNumbers.push(phone);
            }
          }
        }
      }
      
      // Remove duplicates and sort
     uniquePhones = [...new Set(phoneNumbers)].sort();
      
      // If only one number found, auto-fill and search
      if (uniquePhones.length === 1) {
        setPhoneSearch(uniquePhones[0]);
        setShowContactsModal(false);
        setContactsLoading(false);
        
        // Automatically trigger search after a short delay
        setTimeout(() => {
          handlePhoneSearch();
        }, 100);
      } else if (uniquePhones.length > 1) {
        // Multiple numbers found - show selection modal
        setPhoneNumbers(uniquePhones);
       setContactsLoading(false); // Stop loading when showing numbers
      } else {
        // No valid numbers found
        setError('لم يتم العثور على أرقام هواتف صالحة في أسمائك');
        setShowContactsModal(false);
       setContactsLoading(false);
      }
      
    } catch (err) {
      console.error('Error accessing contacts:', err);
      setError(err instanceof Error ? err.message : 'فشل في الوصول للأسماء');
      setShowContactsModal(false);
     setContactsLoading(false);
    }
  };

  const handlePhoneSelect = (phone: string) => {
    setPhoneSearch(phone);
    setShowContactsModal(false);
    // Automatically search after selecting contact
    setTimeout(() => {
      handlePhoneSearch();
    }, 100);
  };

  const fetchVendorProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('No profile found');

      setVendorProfile(profile);
    } catch (err) {
      console.error('Error fetching vendor profile:', err);
      navigate('/home');
    }
  };

  const handlePhoneSearch = async () => {
    // Clean the phone number by removing all spaces and formatting
    const cleanPhone = phoneSearch.replace(/\s+/g, '').trim();
    
    if (!cleanPhone || cleanPhone.length < 7) {
      setError('يجب إدخال رقم هاتف صحيح (7 أرقام على الأقل)');
      return;
    }

    setSearching(true);
    setError(null);
    setFoundCustomer(null);

    try {
      // Use RPC function to search with elevated privileges
      const { data: existingUsers, error: existingError } = await supabase
        .rpc('search_user_by_phone', {
          p_phone: cleanPhone,
          p_country_code: selectedCountryCode,
          p_vendor_smart_key: vendorProfile?.smart_key
        });

      if (existingError) throw existingError;

      if (existingUsers && existingUsers.length > 0) {
        const existingUser = existingUsers[0];
        
        if (existingUser.is_already_linked) {
          // User is already linked - show simple message
          setError('هذا العميل مرتبط بحسابك بالفعل');
          setCustomerSmartKey(existingUser.smart_key);
        } else {
          // User exists but not linked - auto-populate smart key
          setError(null);
          setCustomerSmartKey(existingUser.smart_key);
          setSuccess(`تم العثور على ${existingUser.username} - تم تعبئة الرمز الذكي تلقائياً`);
        }
      } else {
        // User doesn't exist - show invitation option
        setShowInvitationModal(true);
        setInvitationPhone(phoneSearch.trim());
        setInvitationCountryCode(selectedCountryCode);
        setInvitationAddress('');
        setCustomerFirstName('');
        setCustomerLastName('');
        setCustomerAccountType('customer');
      }
    } catch (err) {
      console.error('Error searching for customer:', err);
      setError('فشل في البحث عن العميل');
    } finally {
      setSearching(false);
    }
  };

  const handleSmartKeySearch = async (value: string) => {
    const searchValue = value.toUpperCase();
    setCustomerSmartKey(searchValue);
    setError(null);
    setFoundCustomer(null);
    
    if (searchValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('smart_key, username')
        .ilike('smart_key', `${searchValue}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching smart keys:', err);
      setError('Failed to search for customers');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCreateAndShareInvitation = async () => {
    console.log('=== handleCreateAndShareInvitation called ===');
    console.log('Form values:', {
      invitationPhone,
      customerFirstName,
      customerLastName,
      invitationCountryCode,
      invitationAddress,
      customerAccountType,
      creatingInvitation
    });

    if (!invitationPhone.trim() || !customerFirstName.trim() || !customerLastName.trim() || creatingInvitation) {
      const errorMsg = 'يرجى إدخال جميع الحقول المطلوبة';
      console.log('Validation failed:', errorMsg);
      setError(errorMsg);
      return;
    }

    console.log('Validation passed, setting loading state...');
    setCreatingInvitation(true);
    setError(null);
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session ? 'Authenticated' : 'Not authenticated');

      if (!session) {
        throw new Error('يجب تسجيل الدخول لإنشاء دعوة');
      }

      console.log('Calling create-invited-user Edge Function...');

      // Get the session token for authorization
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        throw new Error('يجب تسجيل الدخول لإنشاء دعوة');
      }

      // Use direct fetch to get proper error messages
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-invited-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          customerFirstName: customerFirstName.trim(),
          customerLastName: customerLastName.trim(),
          customerPhone: invitationPhone.trim(),
          customerCountryCode: invitationCountryCode,
          customerAddress: invitationAddress.trim() || null,
          customerAccountType: customerAccountType
        })
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        console.error('Edge Function returned error');
        throw new Error(data.error || 'فشل في إنشاء الدعوة');
      }

      if (!data.success) {
        throw new Error(data.error || 'فشل في إنشاء الدعوة');
      }

      const invitationUrl = data.invitation_url;

      console.log('Invitation created successfully:', data);
      console.log('Invitation URL:', invitationUrl);
      
      const whatsappMessage = `مرحباً ${customerFirstName}! 👋

${vendorProfile?.username} من ${vendorProfile?.shop_name || 'متجره'} يدعوك للانضمام إلى تطبيق آجل لإدارة الحسابات.

✨ تم إنشاء حسابك مسبقاً بنجاح!
فقط اختر كلمة المرور الخاصة بك لإكمال التسجيل.

📱 استمتع بميزات:
• متابعة حسابك بسهولة
• استلام إشعارات فورية
• طباعة كشوف الحساب

👆 انقر على الرابط لاختيار كلمة المرور:
${invitationUrl}

شكراً لك! 🙏

رمز الدعوة: ${data.code}`;

      const whatsappUrl = `https://wa.me/${invitationCountryCode.replace('+', '')}${invitationPhone}?text=${encodeURIComponent(whatsappMessage)}`;
      
      console.log('WhatsApp URL:', whatsappUrl);
      window.open(whatsappUrl, '_blank');
      setShowInvitationModal(false);
      setPhoneSearch('');
      setInvitationPhone('');
      setInvitationAddress('');
      setCustomerFirstName('');
      setCustomerLastName('');
      setCustomerAccountType('customer');
    } catch (err) {
      console.error('=== ERROR in handleCreateAndShareInvitation ===');
      console.error('Error creating invitation:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      console.error('Error type:', typeof err);
      console.error('Error constructor:', err?.constructor?.name);

      let errorMessage = 'فشل في إنشاء الدعوة';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const errObj = err as any;
        errorMessage = errObj.message || errObj.error || errorMessage;
      }

      console.error('Final error message:', errorMessage);
      setError(errorMessage);
      alert('Error: ' + errorMessage); // Temporary alert for debugging
    } finally {
      console.log('Finally block: resetting loading state');
      setCreatingInvitation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const { data, error } = await supabase
        .rpc('create_vendor_customer_relationship', {
          p_customer_smart_key: customerSmartKey.trim().toUpperCase()
        });

      if (error) throw error;

      setSuccess(true);
      setCustomerSmartKey('');
      setSearchResults([]);
      setShowQrScanner(false);
      setPhoneSearch('');
      
      // Navigate back after successful addition and refresh parent page
      setTimeout(() => {
        setSuccess(false);
        navigate(-1, { replace: true });
      }, 1500);
    } catch (err) {
      console.error('Error adding customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = (result: { text: string } | null) => {
    if (result?.text) {
      setCustomerSmartKey(result.text.toUpperCase());
      setShowQrScanner(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/50"
        >
          <ArrowRight size={24} className="text-gray-800" />
        </button>
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">إضافة عميل</h1>
          <Tooltip text="إضافة عميل جديد لمتجرك. يمكنك البحث بالهاتف أو استخدام الرمز الذكي." />
        </div>
      </div>

      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">عميل جديد</h2>
          <p className="text-gray-600">ابحث بالهاتف أو استخدم الرمز الذكي</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl flex items-center space-x-2 text-red-600">
            <AlertCircle size={20} />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-xl flex items-center space-x-2 text-green-600">
            <AlertCircle size={20} />
            <p className="text-sm">{typeof success === 'string' ? success : 'تمت إضافة العميل بنجاح!'}</p>
          </div>
        )}

        {/* Phone Search Section */}
        <div className="mb-6 p-4 bg-blue-50 rounded-xl">
          <div className="flex items-center space-x-2 mb-4">
            <Phone size={20} className="text-blue-600" />
            <h3 className="font-medium text-blue-800">البحث برقم الهاتف</h3>
          </div>
          
          <div className="space-y-3">
            <div>
              <select
                value={selectedCountryCode}
                onChange={(e) => setSelectedCountryCode(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-blue-200 focus:outline-none focus:border-blue-500 bg-white text-sm"
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.country} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2">
              <input
                type="tel"
                placeholder="رقم الهاتف"
                value={phoneSearch}
                onChange={(e) => {
                  // Remove any automatic formatting/spaces when typing or pasting
                  const cleanValue = e.target.value.replace(/\s+/g, '');
                  setPhoneSearch(cleanValue);
                }}
                onPaste={(e) => {
                  // Handle paste events to ensure clean number storage
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  const cleanValue = pastedText.replace(/\s+/g, '').replace(/\D/g, '');
                  setPhoneSearch(cleanValue);
                }}
                className="flex-1 px-3 py-2 rounded-lg border border-blue-200 focus:outline-none focus:border-blue-500 text-right text-sm"
              />
              {contactsSupported && (
                <button
                  type="button"
                  onClick={handleContactsAccess}
                  disabled={contactsLoading}
                  className="px-3 py-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="اختيار من الأسماء"
                >
                  {contactsLoading ? (
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Users size={16} />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handlePhoneSearch}
                disabled={searching || !phoneSearch.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Search size={16} />
                )}
              </button>
            </div>
          </div>

          {foundCustomer && (
            <div className={`mt-3 p-3 rounded-lg border ${
              foundCustomer.isExisting ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className={`flex items-center space-x-2 mb-2 ${
                foundCustomer.isExisting ? 'text-blue-700' : 'text-green-700'
              }`}>
                <AlertCircle size={16} />
                <span className="text-sm font-medium">
                  {foundCustomer.isExisting ? 'مستخدم موجود!' : 'تم العثور على العميل!'}
                </span>
              </div>
              <p className={`text-sm mt-1 ${
                foundCustomer.isExisting ? 'text-blue-600' : 'text-green-600'
              }`}>
                {foundCustomer.username} - {foundCustomer.smart_key}
              </p>
              {foundCustomer.isExisting && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-blue-800 font-medium">
                    هذا المستخدم مسجل مسبقاً في التطبيق
                  </p>
                  <div className="bg-blue-100 rounded-lg p-3">
                    <p className="text-blue-900 text-xs font-medium mb-1">رمزه الذكي:</p>
                    <p className="text-blue-900 font-mono text-lg font-bold">{foundCustomer.smart_key}</p>
                  </div>
                  <p className="text-blue-700 text-xs">
                    يمكنك استخدام هذا الرمز مباشرة أو أن تطلب منه إضافتك بدلاً من الدعوة
                  </p>
                  <button
                    onClick={() => {
                      setCustomerSmartKey(foundCustomer.smart_key);
                      setFoundCustomer(null);
                      setPhoneSearch('');
                    }}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    استخدام الرمز الذكي: {foundCustomer.smart_key}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* "أو" separator */}
        <div className="text-center my-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">أو</span>
            </div>
          </div>
        </div>

        {/* Smart Key Section */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="relative">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="الرمز الذكي للعميل"
                value={customerSmartKey}
                onChange={(e) => handleSmartKeySearch(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
                pattern="[A-Z0-9]{6}"
                title="يجب أن يكون الرمز الذكي 6 أحرف"
                required
              />
              <button
                type="button"
                onClick={() => setShowQrScanner(!showQrScanner)}
                className="px-4 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <QrCode size={20} />
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.smart_key}
                    type="button"
                    onClick={() => {
                      setCustomerSmartKey(result.smart_key);
                      setSearchResults([]);
                    }}
                    className="w-full px-4 py-3 text-right hover:bg-gray-50 flex justify-between items-center"
                  >
                    <span className="font-medium">{result.smart_key}</span>
                    <span className="text-sm text-gray-500">{result.username}</span>
                  </button>
                ))}
              </div>
            )}

            {searching && !foundCustomer && (
              <div className="absolute right-3 top-3">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {showQrScanner && (
            <div className="relative">
              <div className="absolute -top-2 right-2 z-10">
                <button
                  type="button"
                  onClick={() => setShowQrScanner(false)}
                  className="p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="rounded-xl overflow-hidden">
                <QrReader
                  onError={(err) => console.error(err)}
                  onScan={handleQrScan}
                  style={{ width: '100%' }}
                  facingMode="environment"
                  delay={1000}
                  constraints={{
                    video: { facingMode: "environment" }
                  }}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !customerSmartKey || customerSmartKey.length !== 6}
            className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'جاري إضافة العميل...' : 'إضافة العميل'}
          </button>
        </form>
      </div>

      {/* Contacts Modal */}
      {showContactsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">اختيار رقم هاتف</h3>
              <button 
                onClick={() => setShowContactsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {contactsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 text-sm">جاري الوصول لأرقام الهواتف...</p>
                <p className="text-gray-500 text-xs mt-1">قد تحتاج للسماح بالوصول للجهات</p>
              </div>
            ) : (
              <>
                {/* Phone numbers list */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {phoneNumbers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="mx-auto text-gray-300 mb-2" size={32} />
                      <p className="text-gray-500 text-sm">
                        لا توجد أرقام هواتف في جهات الاتصال
                      </p>
                    </div>
                  ) : (
                    phoneNumbers.map((phone, index) => (
                      <button
                        key={index}
                        onClick={() => handlePhoneSelect(phone)}
                        className="w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-right"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Phone size={16} className="text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-lg font-mono text-gray-900" dir="ltr">{phone}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {phoneNumbers.length > 0 && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-purple-700 text-xs">
                      تم العثور على {phoneNumbers.length} رقم هاتف. اختر الرقم المطلوب للبحث عنه في التطبيق.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">دعوة عميل جديد</h3>
              <button 
                onClick={() => {
                  setShowInvitationModal(false);
                  setError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center space-x-2 text-orange-700 mb-2">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">لم يتم العثور على العميل</span>
              </div>
              <p className="text-sm text-orange-600">
                رقم الهاتف: {invitationCountryCode} {invitationPhone}
              </p>
              <p className="text-sm text-orange-600">
                سيتم إنشاء حساب جديد للعميل وإرسال رابط لاختيار كلمة المرور
              </p>
            </div>

            {/* Customer Information Form */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الأول *</label>
                <input
                  type="text"
                  placeholder="مثال: أحمد"
                  value={customerFirstName}
                  onChange={(e) => setCustomerFirstName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الأخير *</label>
                <input
                  type="text"
                  placeholder="مثال: محمد"
                  value={customerLastName}
                  onChange={(e) => setCustomerLastName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
                  required
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={invitationCountryCode}
                    disabled
                    className="w-20 px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm text-center"
                  />
                  <input
                    type="tel"
                    value={invitationPhone}
                    disabled
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">العنوان (اختياري)</label>
                <input
                  type="text"
                  placeholder="يمكن تركه فارغ ليملأه العميل"
                  value={invitationAddress}
                  onChange={(e) => setInvitationAddress(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نوع الحساب</label>
                <select
                  value={customerAccountType}
                  onChange={(e) => setCustomerAccountType(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm bg-white"
                >
                  <option value="customer">عميل</option>
                  <option value="shop_owner">صاحب متجر</option>
                  <option value="shop_assistant">مساعد متجر</option>
                </select>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">ملاحظة:</span> سيتم إنشاء حساب للعميل مباشرة وسيتم ربطه بحسابك تلقائياً. سيستلم رابط لاختيار كلمة المرور الخاصة به.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowInvitationModal(false);
                  setError(null);
                }}
                disabled={creatingInvitation}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={() => {
                  console.log('Button clicked!');
                  handleCreateAndShareInvitation();
                }}
                disabled={creatingInvitation || !invitationPhone.trim() || !customerFirstName.trim() || !customerLastName.trim()}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Share2 size={18} />
                <span>{creatingInvitation ? 'جاري الإنشاء...' : 'إنشاء الحساب ومشاركة الدعوة'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCustomer;